import { useEffect, useMemo, useCallback } from 'react';
import { useIMask } from 'react-imask';

export const useTextInputMaskedPresenter = ({
  mask,
  unmask,
  name,
  value,
  onChange,
  onComplete,
  onBlur,
}) => {
  const completeHandler = useCallback(
    (masked, data) => {
      const newVal = unmask ? data.unmaskedValue : data.value;
      if (newVal !== value) {
        onComplete(name ? { [name]: newVal } : newVal);
      }
    },
    [unmask, value, onComplete, name]
  );

  const changeHandler = useCallback(
    (masked, data, event) => {
      const newVal = unmask ? data?.unmaskedValue : data?.value;
      if (newVal !== value && event) {
        onChange(name ? { [name]: newVal } : newVal);
      }
    },
    [unmask, value, onChange, name]
  );

  const blurHandler = () => {
    const val = unmask ? unmaskedValue : maskValue;
    onBlur(name ? { [name]: val } : val);
  };

  const iMaskProps = useMemo(() => {
    const data = {
      onAccept: changeHandler,
      onComplete: completeHandler,
      unmask: unmask,
    };
    if (unmask) {
      data.defaultUnmaskedValue = value;
    } else {
      data.defaultValue = value;
    }

    return data;
  }, [unmask, value, onChange, onBlur, onComplete]);

  const {
    ref,
    maskRef,
    value: maskValue,
    unmaskedValue,
    setValue,
    setUnmaskedValue,
  } = useIMask({ mask }, iMaskProps);

  const clearHandler = () => {
    onChange(name ? { [name]: '' } : '');
    onBlur(name ? { [name]: '' } : '');
  };

  const inputValue = useMemo(() => {
    return maskValue || value;
  }, [maskValue || value]);

  useEffect(() => {
    if (value === undefined || value === maskValue || !setValue) {
      return;
    }
    unmask ? setUnmaskedValue(value) : setValue(value);
    maskRef.current.value = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (value && maskRef.current) {
      maskRef.current.value = value;
      maskRef.current.updateValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { value: inputValue, ref, unmaskedValue, clearHandler, blurHandler };
};
